import partition from 'lodash/partition';
import uniqBy from 'lodash/uniqBy';

export interface AudioDeviceOption {
  deviceId: string;
  label: string;
}

export const getMicrophoneDevices = async () => {
  const getDevices = async () => {
    if (!navigator.mediaDevices) {
      return [];
    }
    const rawDevices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = rawDevices
      .filter((device) => device.kind === 'audioinput' && device.deviceId)
      .map((device) => ({
        deviceId: device.deviceId,
        label: device.label,
      }));

    const [labeledDevices, unlabeledDevices] = partition(
      audioDevices,
      (device) => device.label,
    );

    const relabeledDevices = unlabeledDevices.map((device, index) => ({
      ...device,
      label:
        device.deviceId === 'default'
          ? 'Default Audio Input Device'
          : `Audio Input Device ${index + 1}`,
    }));

    const devices = uniqBy(
      [...labeledDevices, ...relabeledDevices],
      (device) => device.deviceId,
    );

    return devices;
  };

  let audioDevices = await getDevices();

  // When no devices are found, try to get them by requesting audio access from the user.
  if (!audioDevices.length) {
    if (!navigator.mediaDevices) {
      return [];
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      audioDevices = await getDevices();

      stream.getTracks().forEach((track) => {
        track.stop();
      });
    } catch (_error) {
      return [];
    }
  }

  return audioDevices satisfies AudioDeviceOption[];
};
